import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../../../context/useAuth';
import { useSocket } from '../../../context/useSocket';
import { isStaff } from '../../../shared/utils/roles';
import { fetchUnseenTherapyOccurrenceRequestsCount } from '../api/therapyOccurrenceRequestsApi';

// Socket event sa beka: broj novih (nevidjenih) zahteva se promenio.
const UNSEEN_COUNT_EVENT = 'requests:unseen-count';

const RequestNotificationsContext = createContext({
  unseenCount: 0,
  setUnseenCount: () => {},
  refreshUnseen: () => {},
});

// Globalno prati broj novih zahteva da bi badge na "Zahtevi" tabu bio vidljiv
// i kada admin nije na toj stranici. Seed preko REST-a, a zatim uzivo preko
// `requests:unseen-count` koji bek salje svim nalozima apoteke.
export const RequestNotificationsProvider = ({ children }) => {
  const { token, user } = useAuth();
  const { socket } = useSocket();
  const canSee = !!token && isStaff(user);

  const [unseenCount, setUnseenCountState] = useState(0);

  // Poslednji pokrenuti seed; stariji odgovori se odbacuju.
  const latestSeedRef = useRef(0);

  const seedUnseen = useCallback(async () => {
    const seedId = latestSeedRef.current + 1;
    latestSeedRef.current = seedId;

    const { data, ok } = await fetchUnseenTherapyOccurrenceRequestsCount();
    if (seedId !== latestSeedRef.current || !ok) return;
    setUnseenCountState(Number(data?.count) || 0);
  }, []);

  // Seed posle logina; reset na logout (ili za naloge bez pristupa zahtevima).
  useEffect(() => {
    if (!canSee) {
      latestSeedRef.current += 1;
      setUnseenCountState(0);
      return;
    }
    seedUnseen();
  }, [canSee, seedUnseen]);

  // Uzivo: svaki event nosi tacan broj. Posle ponovnog povezivanja seed-ujemo
  // ponovo, jer su eventi dok je veza bila prekinuta izgubljeni.
  useEffect(() => {
    if (!socket || !canSee) return undefined;

    const handleCount = (payload) => {
      // Live vrednost je novija od seed-a koji je mozda jos u toku.
      latestSeedRef.current += 1;
      setUnseenCountState(Number(payload?.count) || 0);
    };
    const handleReconnect = () => seedUnseen();

    socket.on(UNSEEN_COUNT_EVENT, handleCount);
    socket.io.on('reconnect', handleReconnect);
    return () => {
      socket.off(UNSEEN_COUNT_EVENT, handleCount);
      socket.io.off('reconnect', handleReconnect);
    };
  }, [canSee, seedUnseen, socket]);

  // Stranica "Zahtevi" postavlja broj koji vrati oznacavanje zahteva kao vidjenih.
  const setUnseenCount = useCallback((count) => {
    latestSeedRef.current += 1;
    setUnseenCountState(Number(count) || 0);
  }, []);

  const refreshUnseen = useCallback(() => {
    if (canSee) seedUnseen();
  }, [canSee, seedUnseen]);

  const value = useMemo(
    () => ({ unseenCount, setUnseenCount, refreshUnseen }),
    [unseenCount, setUnseenCount, refreshUnseen]
  );

  return (
    <RequestNotificationsContext.Provider value={value}>
      {children}
    </RequestNotificationsContext.Provider>
  );
};

RequestNotificationsProvider.propTypes = {
  children: PropTypes.node,
};

export const useRequestNotifications = () =>
  useContext(RequestNotificationsContext);
